import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Session } from '@claude-code-api/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useInterval } from '@/hooks/useInterval';
import { getSessionOpenUrl } from '@/lib/gift-agent';

export default function GiftAgentPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSession = useCallback(async () => {
    try {
      const sessionResponse = await fetch(`/api/sessions/${sessionId}`, { credentials: 'include' });

      if (sessionResponse.ok) {
        const data = await sessionResponse.json();
        setSession(data.data);
      } else {
        navigate('/sessions');
      }
    } catch (error) {
      console.error('Failed to fetch session:', error);
      toast.error('Failed to load session');
    } finally {
      setLoading(false);
    }
  }, [sessionId, navigate]);

  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  useEffect(() => {
    if (!session || (session.runnerType !== 'acpx' && session.runnerType !== 'acpx-experimental') || session.status !== 'running') {
      return;
    }
    // Always funnel first-open through the API wait page so the browser does
    // not land on Traefik/orchestrator before the route is actually reachable.
    window.location.replace(getSessionOpenUrl(session.id));
  }, [session]);

  const pollStatus = useCallback(async () => {
    if (!session || !session.containerId || session.status !== 'running') return;

    try {
      const response = await fetch(`/api/sessions/${sessionId}/status`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.data.sessionStatus === 'stopped') {
          await fetchSession();
        }
      }
    } catch {
      // Silently ignore errors
    }
  }, [session, sessionId, fetchSession]);

  useInterval(pollStatus, 30000);

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
        <div className="space-y-4 w-64">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-48 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Session not found</p>
      </div>
    );
  }

  if (session.runnerType !== 'acpx' && session.runnerType !== 'acpx-experimental') {
    navigate(`/sessions/${sessionId}`, { replace: true });
    return null;
  }

  if (session.status !== 'running') {
    return (
      <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-4 bg-background">
        <p className="text-muted-foreground">This session is not running.</p>
        <button
          onClick={() => navigate(`/sessions/${sessionId}`)}
          className="text-primary hover:underline text-sm"
        >
          View session details
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
      <p className="text-muted-foreground">Opening ACPX workspace...</p>
    </div>
  );
}
